import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, Moon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";

export default function CartDrawer() {
  const { items, setQty, remove, subtotal, drawerOpen, setDrawerOpen } = useCart();
  const navigate = useNavigate();
  const shipping = subtotal >= 75 || subtotal === 0 ? 0 : 6.95;

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)} className="fixed inset-0 z-[70] bg-navy/40 backdrop-blur-sm" data-testid="cart-overlay" />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
            data-testid="cart-drawer" role="dialog" aria-label="Shopping cart">
            <div className="flex items-center justify-between border-b border-blush px-6 py-5">
              <h2 className="font-display text-2xl font-semibold text-navy">Your Buddies</h2>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close cart" data-testid="cart-close-btn"
                className="flex h-10 w-10 items-center justify-center rounded-full text-navy/50 hover:bg-blush">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center" data-testid="cart-empty">
                  <Moon size={44} className="text-lavdeep" />
                  <p className="mt-4 font-display text-xl text-navy">No Buddies yet</p>
                  <p className="mt-2 text-sm text-navy/55">Someone special is waiting to meet your little one.</p>
                  <button onClick={() => { setDrawerOpen(false); navigate("/buddy/luna-the-unicorn"); }} data-testid="cart-empty-cta"
                    className="mt-6 rounded-full bg-pink px-6 py-3 font-display text-sm font-semibold text-navy transition hover:bg-moon">
                    MEET THE BUDDIES
                  </button>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map((item) => (
                    <li key={item.slug} className="flex gap-4 rounded-3xl bg-cream p-4" data-testid={`cart-item-${item.slug}`}>
                      <img src={item.image} alt={item.name} className="h-20 w-16 rounded-2xl object-cover" />
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-display font-semibold text-navy">{item.name}</p>
                          <button onClick={() => remove(item.slug)} aria-label={`Remove ${item.name}`} data-testid={`cart-remove-${item.slug}`}
                            className="text-navy/30 transition hover:text-pink">
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <p className="text-sm text-pinkdeep font-bold">${item.price.toFixed(2)}</p>
                        <div className="mt-2 flex items-center gap-3">
                          <button onClick={() => setQty(item.slug, item.qty - 1)} aria-label="Decrease quantity" data-testid={`cart-minus-${item.slug}`}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-blush bg-white text-navy hover:border-pink">
                            <Minus size={14} />
                          </button>
                          <span className="w-6 text-center font-semibold text-navy" data-testid={`cart-qty-${item.slug}`}>{item.qty}</span>
                          <button onClick={() => setQty(item.slug, item.qty + 1)} aria-label="Increase quantity" data-testid={`cart-plus-${item.slug}`}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-blush bg-white text-navy hover:border-pink">
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-blush bg-blush/50 px-6 py-5">
                <button onClick={() => { setDrawerOpen(false); navigate("/buddy/luna-the-unicorn"); }} data-testid="cart-cross-sell"
                  className="mb-4 w-full rounded-2xl border border-dashed border-pink/50 bg-white py-3 text-sm font-semibold text-pinkdeep transition hover:border-pink">
                  + Add another Buddy — they're better with friends
                </button>
                <div className="flex justify-between text-sm text-navy/60">
                  <span>Subtotal</span><span data-testid="cart-subtotal" className="font-bold text-navy">${subtotal.toFixed(2)}</span>
                </div>
                <div className="mt-1 flex justify-between text-sm text-navy/60">
                  <span>Shipping</span>
                  <span className="font-bold text-navy">{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span>
                </div>
                {shipping > 0 && <p className="mt-1 text-xs text-pinkdeep font-semibold">Free shipping on orders over $75</p>}
                <button onClick={() => { setDrawerOpen(false); navigate("/checkout"); }} data-testid="cart-checkout-btn"
                  className="mt-5 w-full rounded-full bg-pink py-4 font-display text-base font-semibold tracking-wide text-navy transition hover:scale-[1.02] hover:bg-moon">
                  CHECKOUT — ${(subtotal + shipping).toFixed(2)}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
